import {
  createContext,
  useCallback,
  useContext,
  useId,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

interface QuickPanelTrailSlotContextValue {
  registerTrailEnd: (ownerId: string, node: ReactNode | null) => void;
  trailEnd: ReactNode | null;
}

const QuickPanelTrailSlotContext = createContext<QuickPanelTrailSlotContextValue | null>(null);

interface TrailRegistration {
  ownerId: string;
  node: ReactNode;
}

export function QuickPanelTrailSlotProvider({ children }: { children: ReactNode }) {
  const [registrations, setRegistrations] = useState<readonly TrailRegistration[]>([]);

  const registerTrailEnd = useCallback((ownerId: string, node: ReactNode | null) => {
    setRegistrations((current) => {
      const withoutOwner = current.filter((entry) => entry.ownerId !== ownerId);
      if (node === null) {
        return withoutOwner;
      }
      return [...withoutOwner, { ownerId, node }];
    });
  }, []);

  const trailEnd = registrations.at(-1)?.node ?? null;
  const value = useMemo(() => ({ registerTrailEnd, trailEnd }), [registerTrailEnd, trailEnd]);

  return (
    <QuickPanelTrailSlotContext.Provider value={value}>
      {children}
    </QuickPanelTrailSlotContext.Provider>
  );
}

/** Registers content aligned to the right of the current Quick Panel trail. */
export function useQuickPanelTrailSlot(node: ReactNode | null) {
  const context = useContext(QuickPanelTrailSlotContext);
  const ownerId = useId();

  useLayoutEffect(() => {
    if (!context) {
      return;
    }
    context.registerTrailEnd(ownerId, node);
    return () => {
      context.registerTrailEnd(ownerId, null);
    };
  }, [context, node, ownerId]);
}

export function QuickPanelTrailEnd() {
  const context = useContext(QuickPanelTrailSlotContext);
  if (context === null || context.trailEnd === null) {
    return null;
  }
  return <span className="qp-trail__end">{context.trailEnd}</span>;
}
